import { Injectable } from '@angular/core';
import { SymptomValuePipe } from '../../symptoms/pipes/symptom-value.pipe';
import { SymptomsPipe } from '../../symptoms/pipes/symptoms.pipe';
import { SchemaService } from '../../symptoms/services/schema/schema.service';
import { SymptomModes } from '../../symptoms/symptom.model';
interface KeyValueMap {
  [key: string]: string;
}

export interface EyeInformation {
  frequency: number;
  color: string;
  severity: string;
}

interface SignDataClassification {
  leftEye: {};
  rightEye: {};
}
interface ColorDescriptor {
  [color: string]: {
    text: string;
    severity: string;
    colorCode: string; // Hexadecimal color code
  };
}

@Injectable({
  providedIn: 'root'
})
export class PdfSummaryService {
  private severityLevelArray = [
    'Normal',
    'Mild',
    'Mild To Moderate',
    'Moderate',
    'Moderate To Severe',
    'Severe'
  ];

  public severityClassification: {
    [key in 'leftEye' | 'rightEye']: { [key: string]: EyeInformation };
  } = {
    leftEye: {},
    rightEye: {}
  };
  public colorMap: ColorDescriptor = {
    green: {
      text: 'Optimal',
      severity: 'Normal',
      colorCode: '#1ae61a'
    },
    yellow: {
      text: 'Elevated',
      severity: 'Mild',
      colorCode: '#e6e61a'
    },
    orange: {
      text: 'High',
      severity: 'Mild To Moderate',
      colorCode: '#e6b31a'
    },
    yelloworange: {
      text: 'Significant',
      severity: 'Moderate',
      colorCode: '#e6801a'
    },
    orangered: {
      text: 'Urgent',
      severity: 'Moderate To Severe',
      colorCode: '#e64d1a'
    },
    redorange: {
      text: 'Urgent',
      severity: 'Moderate To Severe',
      colorCode: '#e64d1a'
    },
    red: {
      text: 'Critical',
      severity: 'Severe',
      colorCode: '#e61a1a'
    }
  };

  constructor(
    private symptomPipe: SymptomsPipe,
    private schemaService: SchemaService,
    private symptomValuePipe: SymptomValuePipe
  ) {}

  public getSeverityLevel(formKeyValue: KeyValueMap, mode: SymptomModes) {
    this.severityClassification = { leftEye: {}, rightEye: {} };
    if (formKeyValue) {
      const result = this.symptomPipe.transform(formKeyValue);
      result.forEach(signData => {
        const symptomKey = signData.key;

        ['rightEye', 'leftEye'].forEach(sideKey => {
          const side = signData[sideKey];
          if (side) {
            const parsedValue = this.parseValue(side.value);
            const thermalScale = this.schemaService.getThermalScaleConfig(symptomKey, side.method);

            let severityColor = this.getColorFromValue(parsedValue, thermalScale);
            if (severityColor) {
              severityColor = severityColor.replace(/\s/g, '');
              this.severityClassification[sideKey][severityColor] = this.severityClassification[
                sideKey
              ][severityColor]
                ? {
                    frequency: ++this.severityClassification[sideKey][severityColor].frequency,
                    color: this.severityClassification[sideKey][severityColor].color,
                    severity: this.severityClassification[sideKey][severityColor].severity
                  }
                : {
                    frequency: 1,
                    color:
                      (this.colorMap[severityColor] && this.colorMap[severityColor].colorCode) ||
                      '',
                    severity:
                      (this.colorMap[severityColor] && this.colorMap[severityColor].severity) || ''
                  };
            }
          }
        });
      });
    }
  }


  public getColorFromValue(value: number | string, thermalScale: any = {}): string | null {
    if (thermalScale === null || thermalScale === undefined) return null;

    for (const [color, condition] of Object.entries(thermalScale)) {
      if (color === 'linear') {
        return this.mapLinearValueToColor(+value);
      }
      if (color === 'hide') {
        if (typeof condition === 'string' && condition.startsWith('>')) {
          const comparValue = +condition.slice(1);
          if (+value > comparValue) {
            return '';
          }
        }
      }

      if (typeof condition === 'string') {
        if (condition.includes('-')) {
          let [min, max] = condition.split('-').map(Number);

          if (min > max) {
            const temp = max;
            max = min;
            min = temp;
          }

          if (typeof value === 'number' && value >= min && value <= max) {
            return color;
          }
        } else if (condition.startsWith('>=')) {
          const compareValue = +condition.slice(2);
          if (typeof value === 'number' && value >= compareValue) {
            return color;
          }
        } else if (condition.startsWith('>')) {
          const compareValue = +condition.slice(1);
          if (typeof value === 'number' && value > compareValue) {
            return color;
          }
        } else if (condition.startsWith('<')) {
          const compareValue = +condition.slice(1);
          if (typeof value === 'number' && value < compareValue) {
            return color;
          }
        } else if (value === condition) {
          return color;
        }
      }
    }

    return null;
  }

  private mapLinearValueToColor(value: number): string {
    if (value > 10) return 'green';

    const severity = 10 - value;
    if (severity <= 2) {
      return 'green';
    } else if (severity <= 5) {
      return 'yellow';
    } else if (severity <= 8) {
      return 'orange';
    } else {
      return 'red';
    }
  }
  parseValue(val: any) {
    if (typeof val === 'object') {
      return Object.entries(val)
        .map(([k, v]) => `${k[0].toUpperCase() + k.substring(1)}: ${v}`)
        .join(', ');
    }
    return val;
  }

  sortOrder(severityDistribution) {
    return Object.keys(severityDistribution)
      .sort((a, b) => this.severityLevelArray.indexOf(a) - this.severityLevelArray.indexOf(b))
      .reduce((acc, key) => {
        acc[key] = severityDistribution[key];
        return acc;
      }, {});
  }
}
